import styled, { useTheme } from 'styled-components';
import {
  BodyText,
  Button,
  FlexColumn,
  FlexRow,
  Input,
  PageTile,
  SubtitleText,
  SvgIcon,
  TableData,
  Tooltip
} from '@make-software/cspr-design';
import { Link } from 'react-router-dom';

export const centerModalStyles = {
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  borderRadius: 'none',
  padding: '32px 24px 24px 24px',
  top: '50%',
  transform: 'translate(-50%, -50%)'
};

export const LayoutContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    margin: '40px',
    width: ['100%', '70%', '95%']
  })
);

export const TablePageContainer = styled(PageTile)(({ theme }) =>
  theme.withMedia({
    width: '100%'
  })
);

export const PageContainer = styled(PageTile)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    padding: ['16px 0 24px 0']
  })
);

export const ViewPageContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    width: '95%',
    padding: ['0 24px 0 24px']
  })
);

export const EditButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: '130px',
    margin: ['12px 12px 0 12px']
  })
);

export const StyledTableFiltersBlock = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    flexWrap: 'wrap',
    gap: 8,
    align: 'start',
    margin: ['12px']
  })
);

export const FormContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    width: ['230px', '300px', '450px']
  })
);

export const LoadingContainer = styled(PageContainer)(() => ({
  marginTop: '20px'
}));

export const ModalContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    // width: ['300px', '400px', '446px'],
    width: ['330px', '400px', '616px'],
    background: theme.styleguideColors.backgroundPrimary,
    borderColor: theme.styleguideColors.backgroundPrimary,
    overflowY: ['scroll', 'unset', 'unset']
    // height: ['500px', 'unset']
  })
);

export const StyledCaption = styled.div(({ theme }) =>
  theme.withMedia({
    textAlign: 'center',
    marginBottom: '16px'
  })
);

export const StyledCaptionText = styled(SubtitleText)(({ theme }) =>
  theme.withMedia({
    fontWeight: [600, 600, 700],
    fontSize: ['20px', '24px', '24px'],
    color: theme.styleguideColors.contentPrimary
  })
);

export const StyledInput = styled(Input)<{ withBorder?: boolean }>(({ theme, withBorder }) =>
  theme.withMedia({
    width: '100%',
    'div:nth-child(2)': {
      border: withBorder ? `1px solid ${theme.styleguideColors.contentSecondary}` : 'none'
    }
  })
);

export const ButtonsContainer = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    marginTop: ['16px', '32px'],
    flexDirection: ['column', 'row', 'row'],
    '& > * + *': {
      marginTop: [16, 0]
    }
  })
);

export const StyledConfirmButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['100%', '45%', '47%'],
    backgroundColor: theme.styleguideColors.fillPrimaryBlue,
    ':hover': {
      background: theme.styleguideColors.fillPrimaryBlueHover
    },
    ':active': {
      background: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

export const StyledDismissButton = styled(Button)<{ fullWidth?: boolean }>(({ theme, fullWidth }) =>
  theme.withMedia({
    width: fullWidth ? '100%' : ['100%', '45%', '47%'],
    color: theme.styleguideColors.contentBlue,
    ':hover': {
      background: theme.styleguideColors.fillSecondaryBlueHover
    },
    ':active': {
      background: theme.styleguideColors.fillSecondaryBlueClick
    }
  })
);

export const StyledRemoveButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['100%', '45%', '47%'],
    backgroundColor: theme.styleguideColors.fillPrimaryRed,
    ':hover': {
      background: theme.styleguideColors.fillPrimaryRedHover
    },
    ':active': {
      background: theme.styleguideColors.fillPrimaryRedClick
    }
  })
);

export const TableValidationErrorWrapper = styled(FlexRow)<{ top?: string }>(({ theme, top }) =>
  theme.withMedia({
    position: 'absolute',
    top: ['unset', 'unset', top ? top : '30px'],
    width: '100%',
    right: '20px'
  })
);
export const TableValidationText = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    fontSize: '11px',
    position: 'relative',
    height: '0',
    color: theme.styleguideColors.borderRed
  })
);

export const ErrorTextContainer = styled(FlexRow)<{ top?: string }>(({ theme, top }) =>
  theme.withMedia({})
);
export const ErrorText = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    fontSize: '11px',
    lineHeight: '14px',
    color: theme.styleguideColors.borderRed
  })
);

export const StyledToggle = styled.div(({ theme }) =>
  theme.withMedia({
    marginTop: '32px',
    padding: '16px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px solid ${theme.styleguideColors.borderSecondary}`,
    borderBottom: `1px solid ${theme.styleguideColors.borderSecondary}`
  })
);

export const StyledLink = styled(Link)(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentBlue
  })
);

export const TOGGLE_ACTIVE_COLOR = '#0021A5';

export const StatisticsFiltersStyles = () => ({
  ...DropdownStyles({ menuMaxHeight: '300px' }),
  container: (base: any) => ({ ...base, minWidth: '200px', maxWidth: '650px' })
});

export const AppsFiltersStyles = () => ({
  ...DropdownStyles({ menuMaxHeight: '300px', menuPosition: 'absolute' }),
  container: (base: any) => ({ ...base, minWidth: '200px', maxWidth: '350px' })
});

export interface DropdownStylesOptions {
  menuMaxHeight?: string;
  controlMinWidth?: string;
  menuPosition?: 'relative' | 'absolute';
}

export const DropdownStyles = (opts?: DropdownStylesOptions) => {
  const theme = useTheme();
  const { menuMaxHeight = '160px', controlMinWidth = '240px', menuPosition = 'relative' } = opts ?? {};
  return {
    control: (base: any, state: any) => ({
      ...base,
      background: theme.styleguideColors.fillSecondary,
      border: 'none',
      padding: '0',
      '&:focus-visible': {
        outline: 'none !important'
      },
      fontSize: '14px',
      boxShadow: state.isFocused ? null : null,
      minWidth: controlMinWidth,
      svg: {
        width: '18px',
        height: '18px',
        fontWeight: '400',
        '&:hover': {
          path: {
            color: 'red',
            cursor: 'pointer'
          }
        }
      }
    }),
    menu: (styles: any) => ({
      ...styles,
      position: menuPosition
    }),
    menuList: (styles: any) => ({
      ...styles,
      background: theme.styleguideColors.fillSecondary,
      maxHeight: menuMaxHeight
    }),
    valueContainer: (styles: any) => ({
      ...styles,
      padding: '4px 8px',
      gap: '4px',
      flexWrap: 'wrap'
    }),
    multiValue: (styles: any) => ({
      ...styles,
      borderRadius: '30px',
      padding: '2px 8px',
      marginRight: '4px',
      marginBottom: '4px',
      fontSize: '14px'
    }),
    option: (styles: any, state: any) => ({
      ...styles,
      fontSize: '14px',
      background: theme.styleguideColors.fillSecondary,
      fontWeight: state.isSelected ? 600 : 400,
      borderRadius: '10px',
      color: '#000',
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: '#0021a514'
      }
    })
  };
};

export const StyledTooltip = styled(Tooltip)(({ theme }) =>
  theme.withMedia({
    padding: '16px'
  })
);

export const StyledIconLink = styled(SvgIcon)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer',
    '&:hover': {
      path: {
        fill: '#4666e9'
      }
    }
  })
);

export const StyledChipsTag = styled(FlexRow)<{ color?: string }>(({ theme, color }) =>
  theme.withMedia({
    alignItems: 'center',
    padding: '0px 8px',
    background: color
      ? `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.1)`
      : theme.styleguideColors.fillSecondary,
    borderRadius: '30px',
    height: 25,
    width: 'max-content'
  })
);

export const StyledDescription = styled.div(({ theme }) =>
  theme.withMedia({
    fontSize: '13px',
    lineHeight: '20px',
    color: theme.styleguideColors.contentSecondary,
    width: ['150px', '150px', '150px', '100%'],
    overflow: 'hidden',

    // Mobile: single-line truncation
    whiteSpace: ['nowrap', 'nowrap', 'nowrap', 'unset'],
    textOverflow: ['ellipsis', 'ellipsis', 'ellipsis', 'unset'],

    // Desktop: multi-line clamp
    display: ['block', 'block', 'block', '-webkit-box'],
    WebkitLineClamp: [undefined, undefined, undefined, 3],
    WebkitBoxOrient: [undefined, undefined, undefined, 'vertical']
  })
);

export const SourceInfoWrapper = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    backgroundColor: theme.styleguideColors.fillSecondary,
    width: '100%',
    padding: '8px 16px',
    height: ['66px', '72px', '80px'],
    borderRadius: '4px'
  })
);

export const NoEntityInfoWrapper = styled.div(({ theme }) =>
  theme.withMedia({
    textAlign: 'center',
    fontSize: '14px',
    padding: '16px',
    fontWeight: '400',
    borderRadius: '4px',
    lineHeight: '20px',
    display: 'block',
    color: theme.styleguideColors.contentSecondary,
    background: '#f3f6c5'
  })
);

export const StyledCategoryPlaceholder = styled.div(({ theme }) =>
  theme.withMedia({
    fontSize: '14px',
    color: theme.styleguideColors.contentSecondary,
    position: 'relative',
    top: '4px',
    height: '21px'
  })
);

export const StyledCardRow = styled.tr<{ $dragging?: boolean }>`
  background: #fff;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.06);
  border-radius: 6px; /* visual target; effective via cell radii */
  will-change: transform, box-shadow;
  transition:
    box-shadow 0.2s ease,
    background 0.2s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }

  /* round the “card” by rounding the edge cells */

  & > td:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
    overflow: hidden;
  }

  & > td:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    overflow: hidden;
  }

  ${({ $dragging }) =>
    $dragging &&
    `
      box-shadow: 0 10px 24px rgba(0,0,0,0.14);
      background: #ffffff;
    `}
`;

export const StyledSvgIcon = styled(SvgIcon)(({ theme }) =>
  theme.withMedia({
    ':hover': {
      cursor: 'pointer',
      path: {
        fill: theme.styleguideColors.contentSecondary
      }
    }
  })
);

export const StyledTableData = styled(TableData)(({ theme }) =>
  theme.withMedia({
    paddingTop: '12px',
    alignContent: 'flex-start'
  })
);

export const RotatingIcon = styled(StyledSvgIcon)<{ $open: boolean }>`
  transition: transform 200ms ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

export const ArrowButton = styled.button(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer',
    padding: '0 8px',
    border: 'none',
    color: 'darkGray',
    width: '24px',
    height: '24px',
    background: 'transparent',
    display: 'inline - flex',
    alignItems: 'center',
    justifyContent: 'center'
  })
);

export const StatsLink = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentBlue,
    marginRight: '8px',
    ':hover': {
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  })
);
